import React, { useCallback, useEffect, useState } from 'react';
import { supabaseAdmin } from '../config/supabase';
import { Calendar, Newspaper, MapPin } from 'lucide-react';
import './VisitCounts.css';

export default function VisitCounts() {
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [eventsWithCounts, setEventsWithCounts] = useState([]);
  const [newsWithCounts, setNewsWithCounts] = useState([]);
  const [placesWithCounts, setPlacesWithCounts] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      let viewsQuery = supabaseAdmin.from('content_unique_views').select('entity_type, entity_id');
      if (dateFrom) viewsQuery = viewsQuery.gte('created_at', `${dateFrom}T00:00:00.000Z`);
      if (dateTo) viewsQuery = viewsQuery.lte('created_at', `${dateTo}T23:59:59.999Z`);

      const [viewsRes, eventsRes, newsRes, placesRes] = await Promise.all([
        viewsQuery,
        supabaseAdmin.from('events').select('id, title').eq('status', 'published').order('start_date', { ascending: false }),
        supabaseAdmin.from('news').select('id, title').eq('status', 'published').order('published_at', { ascending: false }),
        supabaseAdmin.from('interactive_map_places').select('id, name').order('name', { ascending: true }),
      ]);

      if (viewsRes.error) console.warn('VisitCounts: content_unique_views', viewsRes.error);
      if (eventsRes.error) console.warn('VisitCounts: events', eventsRes.error);
      if (newsRes.error) console.warn('VisitCounts: news', newsRes.error);
      if (placesRes.error) console.warn('VisitCounts: places', placesRes.error);

      const views = viewsRes.data || [];
      const events = eventsRes.data || [];
      const news = newsRes.data || [];
      const places = placesRes.data || [];

      const countByEntity = {};
      views.forEach((v) => {
        const key = `${v.entity_type}:${v.entity_id}`;
        countByEntity[key] = (countByEntity[key] || 0) + 1;
      });

      const getCount = (type, id) => countByEntity[`${type}:${id}`] || 0;

      setEventsWithCounts(events.map((e) => ({ ...e, unique_views: getCount('event', e.id) })));
      setNewsWithCounts(news.map((n) => ({ ...n, unique_views: getCount('news', n.id) })));
      setPlacesWithCounts(places.map((p) => ({ ...p, unique_views: getCount('place', p.id) })));
    } catch (err) {
      console.error('VisitCounts load error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <div className="visit-counts-page"><div className="loading">Ładowanie...</div></div>;
  }

  return (
    <div className="visit-counts-page">
      <div className="page-header">
        <h2>Liczba odwiedzin</h2>
      </div>
      <p className="visit-counts-description">
        Unikalne wyświetlenia: każde wyświetlenie jest liczone raz na użytkownika (bez ponownego naliczania przy odświeżeniu lub ponownym wejściu).
      </p>

      <div className="visit-counts-filters card">
        <div className="visit-counts-filters-row">
          <div className="visit-counts-filter-group">
            <label className="form-label">Data od</label>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="visit-counts-filter-group">
            <label className="form-label">Data do</label>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="visit-counts-filter-group visit-counts-filter-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
            >
              Wyczyść filtr
            </button>
          </div>
        </div>
        {(dateFrom || dateTo) && (
          <p className="visit-counts-filter-hint">
            Wyświetlanie wyświetleń z okresu: {dateFrom || '…'} – {dateTo || '…'}
          </p>
        )}
      </div>

      <div className="visit-counts-sections">
        <section className="card visit-counts-section">
          <h3><Calendar size={20} /> Wydarzenia</h3>
          <div className="visit-counts-table-wrap">
            <table className="table visit-counts-table">
              <thead>
                <tr>
                  <th>Tytuł</th>
                  <th className="col-count">Liczba odwiedzin</th>
                </tr>
              </thead>
              <tbody>
                {eventsWithCounts.length === 0 ? (
                  <tr><td colSpan="2" className="empty-state">Brak opublikowanych wydarzeń</td></tr>
                ) : (
                  eventsWithCounts.map((row) => (
                    <tr key={row.id}>
                      <td>{row.title || '—'}</td>
                      <td className="col-count">{row.unique_views}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card visit-counts-section">
          <h3><Newspaper size={20} /> Aktualności</h3>
          <div className="visit-counts-table-wrap">
            <table className="table visit-counts-table">
              <thead>
                <tr>
                  <th>Tytuł</th>
                  <th className="col-count">Liczba odwiedzin</th>
                </tr>
              </thead>
              <tbody>
                {newsWithCounts.length === 0 ? (
                  <tr><td colSpan="2" className="empty-state">Brak opublikowanych aktualności</td></tr>
                ) : (
                  newsWithCounts.map((row) => (
                    <tr key={row.id}>
                      <td>{row.title || '—'}</td>
                      <td className="col-count">{row.unique_views}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card visit-counts-section">
          <h3><MapPin size={20} /> Miejsca na mapie interaktywnej</h3>
          <div className="visit-counts-table-wrap">
            <table className="table visit-counts-table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th className="col-count">Liczba odwiedzin</th>
                </tr>
              </thead>
              <tbody>
                {placesWithCounts.length === 0 ? (
                  <tr><td colSpan="2" className="empty-state">Brak miejsc</td></tr>
                ) : (
                  placesWithCounts.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name || '—'}</td>
                      <td className="col-count">{row.unique_views}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
